import ReleaseNotes from '@rio-cloud/rio-uikit/ReleaseNotes';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { extractLanguage } from '@/configuration/lang/lang';
import type { DescriptionItem, WhatsNewContent, WhatsNewData } from '@/features/userManual/components/WhatsNew/types';
import { formatUrl, t } from '@/lib/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useWhatsNewStore } from '@/stores/whatsNewStore';

const formattedMessageValues = {
    b: (content: ReactNode) => <b>{content}</b>,
    i: (content: ReactNode) => <i>{content}</i>,
    ul: (content: ReactNode) => (
        <ul style={{ listStyleType: 'initial' }} className='margin-left-15 margin-top-0'>
            {content}
        </ul>
    ),
    li: (content: ReactNode) => <li>{content}</li>,
};

export const WhatsNewDescriptionItem = ({ item }: { item: DescriptionItem }) => {
    const intl = useIntl();
    const userLanguage = extractLanguage(intl.locale);

    return (
        <FormattedMessage
            id={item.key}
            values={{
                ...formattedMessageValues,
                link: content =>
                    item.link ? (
                        <a
                            className='text-color-primary'
                            href={formatUrl(item.link, userLanguage)}
                            target='_blank'
                            rel='noreferrer'
                        >
                            {content}
                        </a>
                    ) : (
                        content
                    ),
            }}
        />
    );
};

const WhatsNewContentItem = ({ content }: { content: WhatsNewContent }) => {
    const [imageLoading, setImageLoading] = useState(!!content.image);

    return (
        <div key={content.title}>
            {content.isReleaseNotes && (
                <div className='position-relative width-100pct' style={{ maxHeight: '40px' }}>
                    <img
                        src='/whatsNew/releaseNotesTopBar.webp'
                        alt=''
                        className='width-100pct height-100pct'
                        data-testid='release-notes-image'
                    />
                    <div className='position-absolute inset-0 padding-left-25'>
                        <span
                            className='text-color-white text-bold margin-y-0 height-100pct display-flex align-items-center'
                            style={{
                                fontSize: 'clamp(14px, 2vw, 24px)',
                            }}
                        >
                            {t('userManual.whatsNew.newFeature')}
                        </span>
                    </div>
                </div>
            )}

            {content.image && (
                <div className='position-relative width-100pct'>
                    {imageLoading && (
                        <div className='display-flex justify-content-center align-items-center min-height-400'>
                            <Spinner />
                        </div>
                    )}
                    <img
                        src={content.image.src}
                        alt={t(content.image.alt ?? '')}
                        className='width-100pct height-100pct'
                        style={{ objectFit: 'contain', display: imageLoading ? 'none' : 'block' }}
                        onLoad={() => setImageLoading(false)}
                    />
                </div>
            )}
            <div className='margin-top-15 margin-bottom-10 text-size-h4'>
                <FormattedMessage id={content.title} />
            </div>
            <div>
                {content.descriptionItems.map((item, index) => (
                    <div key={index} className={index > 0 ? 'margin-top-25' : ''}>
                        <WhatsNewDescriptionItem item={item} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const WhatsNewPageDialogContent = () => {
    const intl = useIntl();
    const { userProfile } = useAccountStore.getState();
    const { dismissNotification, filteredWhatsNewData } = useWhatsNewStore();

    useEffect(() => {
        dismissNotification(userProfile?.email, filteredWhatsNewData[0]?.version);
    }, []);

    // Uses the filtered WhatsNew array based on their feature toggles. Then it reduces the list into the object used by the ReleaseNotes component
    const releaseNotesData = filteredWhatsNewData.reduce(
        (releaseNotes, item): WhatsNewData => ({
            ...releaseNotes,
            [intl.formatMessage({ id: item.version })]: {
                date: item.date,
                content: <WhatsNewContentItem content={item.content} />,
            },
        }),
        {}
    );

    return (
        <>
            <h4
                data-testid='whats-new-modal-title'
                className='text-normal text-transform-none margin-top-20 margin-bottom-15'
            >
                <FormattedMessage id='userManual.whatsNew.title' />
            </h4>
            <ReleaseNotes releaseNotes={releaseNotesData} />
        </>
    );
};

export default WhatsNewPageDialogContent;
